import React, { useState, useCallback, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { Card, Form, Icon, Select, Tooltip, Modal, Badge } from 'antd'
import _isArray from 'lodash/isArray'
import _isFunction from 'lodash/isFunction'
import _cloneDeep from 'lodash/cloneDeep'
import uuid from 'uuid/v4'
import I18n from 'i18n-js'

import { FontAwesomeIcon } from '../common'
import MutationFormErrors from './MutationFormErrors'

const { Option } = Select

const DragHandle = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  margin: 0 12px;
`

const ActionButton = styled.span`
  cursor: pointer;
  margin-left: 8px;
  font-size: 16px;
  color: rgba(0, 0, 0, 0.65);
  background-color: #f0f0f0;
  border-radius: 4px;
  padding: 6px 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #1890ff;
    background-color: #e6f7ff;
  }
`

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 16px;
`

const dragAndDropListItemCard = ({ itemMargin, ...rest }) => (<Card {...rest} />)
const DragAndDropListItemCard = styled(dragAndDropListItemCard)`
  margin: ${({ itemMargin }) => itemMargin};
`

const ItemOrderCounter = styled(Badge)`
  display: flex;
  align-items: center;
  margin-right: 12px;

  .ant-badge-count {
    background-color: #1890ff;
    font-size: 14px;
    height: 24px;
    width: 24px;
    line-height: 24px;
  }
`

const DefaultItemComponent = ({ index, item }) => (
  <h3 style={{ margin: 0 }}>{item.label}</h3>
)

const AddItemContainer = styled.div`
  display: flex;
  margin-bottom: 10px;
  .ant-select {
    flex: 1;
    margin-right: 10px;
  }
`

const EditItemModal = ({ visible, item, onCancel, onSave, options, disabled }) => {
  const [selectedOption, setSelectedOption] = useState(item?.value || null)

  const handleOptionChange = useCallback((value) => {
    setSelectedOption(value)
  }, [])

  const handleSave = useCallback(() => {
    const selectedOptionData = options.find(option => option.value === selectedOption)
    if (selectedOptionData) {
      onSave({
        ...item,
        value: selectedOption,
        label: selectedOptionData.label
      })
    }
  }, [item, onSave, options, selectedOption])

  // Reset selected option when modal opens with a new item
  React.useEffect(() => {
    if (visible && item) {
      setSelectedOption(item.value)
    }
  }, [visible, item])

  return (
    <Modal
      title={I18n.t('common.edit')}
      visible={visible}
      onCancel={onCancel}
      onOk={handleSave}
      okButtonProps={{ disabled: !selectedOption || disabled }}
      destroyOnClose
    >
      <Select
        value={selectedOption}
        onChange={handleOptionChange}
        disabled={disabled}
        showSearch
        style={{ width: '100%' }}
        optionFilterProp='children'
      >
        {options.map(option => (
          <Option key={option.value} value={option.value}>{option.label}</Option>
        ))}
      </Select>
    </Modal>
  )
}

const DragAndDropListItem = ({
  index,
  item,
  dragging,
  dragHandleProps,
  update,
  remove,
  itemComponent: ItemComponent = DefaultItemComponent,
  itemMargin,
  disabled,
  allOptions,
  showEdit,
  showPreview,
  onPreviewClick,
  ...rest
}) => {
  const { id } = item
  const [editing, setEditing] = useState(false)

  const onEditClick = useCallback(() => {
    setEditing(true)
  }, [])

  const closeEditModal = useCallback(() => {
    setEditing(false)
  }, [])

  const handleSaveEdit = useCallback((updatedItem) => {
    update(updatedItem)
    setEditing(false)
  }, [update])

  const onRemoveClick = useCallback(() => remove(id), [remove, id])

  const handlePreviewClick = useCallback(() => {
    if (_isFunction(onPreviewClick)) {
      onPreviewClick(item)
    }
  }, [onPreviewClick, item])

  return (
    <>
      <DragAndDropListItemCard itemMargin={itemMargin}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ItemOrderCounter count={index + 1} overflowCount={999} />
          <DragHandle {...dragHandleProps}>
            <FontAwesomeIcon icon='grip-horizontal' />
          </DragHandle>
          <div style={{ flex: 1 }}>
            <ItemComponent {...{ index, item, editing, setEditing, dragging, update, disabled, ...rest }} />
          </div>
          <ActionsContainer>
            {showEdit && (
              <Tooltip title={I18n.t('common.edit')} placement='top'>
                <ActionButton onClick={onEditClick} disabled={disabled}>
                  <Icon type='edit' />
                </ActionButton>
              </Tooltip>
            )}

            {showPreview && _isFunction(onPreviewClick) && (
              <Tooltip title={I18n.t('common.preview')} placement='top'>
                <ActionButton onClick={handlePreviewClick} disabled={disabled}>
                  <Icon type='eye' />
                </ActionButton>
              </Tooltip>
            )}

            <Tooltip title={I18n.t('common.delete')} placement='top'>
              <ActionButton onClick={onRemoveClick} disabled={disabled}>
                <Icon type='delete' />
              </ActionButton>
            </Tooltip>
          </ActionsContainer>
        </div>
      </DragAndDropListItemCard>

      <EditItemModal
        visible={editing}
        item={item}
        onCancel={closeEditModal}
        onSave={handleSaveEdit}
        options={allOptions}
        disabled={disabled}
      />
    </>
  )
}

const MutationFormDragAndDropList = React.forwardRef(({
  id, value = [], onChange = () => {}, errors = [], visible = false,
  label, extra, formItemStyle, required, placeholder, disabled,
  options = [], itemComponent, itemTemplate, droppableId, itemName, itemMargin = '10px 0',
  showEdit = true, showPreview = false, onPreviewClick
}, ref) => {
  const [selectedOption, setSelectedOption] = useState(null)

  const items = _isArray(value) ? value : []

  useImperativeHandle(ref, () => ({
    validate: (formValues, errors) => {
      return errors
    }
  }), [])

  const generateItemId = useCallback(() => {
    let id
    while (!id) {
      const possibleId = uuid()
      const alreadyUsed = items.some(item => item.id === possibleId)
      if (!alreadyUsed) {
        id = possibleId
      }
    }
    return id
  }, [items])

  const handleDragEnd = useCallback((result) => {
    if (result.source && result.destination) {
      const list = [...items]
      const [item] = list.splice(result.source.index, 1)
      list.splice(result.destination.index, 0, item)
      onChange(id, list)
    }
  }, [id, items, onChange])

  const handleOptionChange = useCallback((value) => {
    setSelectedOption(value)
  }, [])

  const handleSelect = useCallback((value) => {
    const selectedOptionData = options.find(option => option.value === value)
    if (selectedOptionData) {
      const newItem = {
        ...(_cloneDeep(itemTemplate || {})),
        id: generateItemId(),
        value,
        label: selectedOptionData.label
      }

      onChange(id, [...items, newItem])
      setSelectedOption(null)
    }
  }, [id, items, onChange, options, itemTemplate, generateItemId])

  const updateItem = useCallback((item) => {
    const list = [...items]
    const index = list.findIndex(i => i.id === item.id)
    if (index !== -1) {
      list[index] = item
      onChange(id, list)
    }
  }, [id, items, onChange])

  const removeItem = useCallback((itemId) => {
    const list = [...items]
    const index = list.findIndex(item => item.id === itemId)
    if (index !== -1) {
      list.splice(index, 1)
      onChange(id, list)
    }
  }, [id, items, onChange])

  const filterOptions = useCallback(() => {
    // Filter out options that are already selected
    const selectedValues = items.map(item => item.value)
    return options.filter(option => !selectedValues.includes(option.value))
  }, [items, options])

  // For editing, we need all options, not just the unselected ones
  const getAllEditableOptions = useCallback(() => {
    // For edit, we include all options, plus the currently selected option for the item being edited
    return options
  }, [options])

  if (!visible) {
    return null
  }

  const showErrors = errors.length > 0
  const availableOptions = filterOptions()
  const allOptions = getAllEditableOptions()

  return (
    <Form.Item
      label={label}
      style={formItemStyle}
      extra={extra}
      required={required}
      validateStatus={showErrors ? 'error' : undefined}
      help={showErrors ? <MutationFormErrors visible={showErrors} errors={errors} /> : null}
    >
      <AddItemContainer>
        <Select
          value={selectedOption}
          onChange={handleOptionChange}
          onSelect={handleSelect}
          disabled={disabled || availableOptions.length === 0}
          showSearch
          optionFilterProp='children'
          style={{ width: '100%' }}
        >
          {availableOptions.map(option => (
            <Option key={option.value} value={option.value}>{option.label}</Option>
          ))}
        </Select>
      </AddItemContainer>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId={droppableId || `droppable-${id}`}>
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {items.map((item, index) => (
                <Draggable
                  key={item.id}
                  draggableId={item.id}
                  index={index}
                  isDragDisabled={disabled}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={provided.draggableProps.style}
                    >
                      <DragAndDropListItem
                        index={index}
                        item={item}
                        update={updateItem}
                        remove={removeItem}
                        itemComponent={itemComponent}
                        dragging={snapshot.isDragging}
                        dragHandleProps={provided.dragHandleProps}
                        itemMargin={itemMargin}
                        disabled={disabled}
                        allOptions={allOptions}
                        showEdit={showEdit}
                        showPreview={showPreview}
                        onPreviewClick={onPreviewClick}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Form.Item>
  )
})

MutationFormDragAndDropList.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  placeholder: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func,
  extra: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.node, PropTypes.func]),
  formItemStyle: PropTypes.object,
  disabled: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.any.isRequired,
    label: PropTypes.node.isRequired
  })),
  itemComponent: PropTypes.elementType,
  itemTemplate: PropTypes.object,
  droppableId: PropTypes.string,
  itemName: PropTypes.string,
  itemMargin: PropTypes.string,
  showEdit: PropTypes.bool,
  showPreview: PropTypes.bool,
  onPreviewClick: PropTypes.func
}

MutationFormDragAndDropList.defaultProps = {
  id: '',
  label: null,
  required: false,
  placeholder: '',
  value: [],
  onChange: () => {},
  options: [],
  disabled: false,
  itemMargin: '10px 0',
  showEdit: true,
  showPreview: false
}

export default MutationFormDragAndDropList
